/* import __COLOCATED_TEMPLATE__ from './attribute-summaries.hbs'; */
/* RESPONSIBLE TEAM: team-ai-insights */
import Component from '@glimmer/component';
import type { Job } from 'embercom/components/reporting/automation/ai-insights/optimize/jobs';
import { type LogicalFilter } from 'embercom/components/reporting/custom/filters';
import type Range from 'embercom/models/reporting/range';
import { type SummaryTab } from './conversation-explore-panel';
import { use } from 'ember-resources/util/function-resource';
import { staticDataResource } from 'embercom/lib/reporting/static-data-resource';
import { type ReportState } from 'embercom/components/reporting/custom/report/chart-card';
import type { ChartSeriesDefinition } from 'embercom/components/reporting/automation/ai-insights/fin-insights/jobs';
import { action } from '@ember/object';
import type ContentSuggestionsService from 'embercom/services/content-suggestions-service';
import type ReportingChartService from 'embercom/services/reporting-chart-service';
import { inject as service } from '@ember/service';
import type ContentWrapper from 'embercom/models/content-service/content-wrapper';
import type TaskSuggestion from 'embercom/models/content-service/task-suggestion';
import { restartableTask } from 'ember-concurrency-decorators';
import { type TaskGenerator } from 'ember-concurrency';
import { taskFor } from 'ember-concurrency-ts';
import { tracked } from '@glimmer/tracking';
import { TrackedMap } from 'tracked-built-ins';

interface Signature {
  Args: {
    selectedJob: Job;
    dateRange: Range;
    onShowConversationExplorer: (
      selectedConversationId: number | null,
      filters: LogicalFilter,
      attributeValuesToExplore: string[],
      summaryType: SummaryTab,
      summaryText: string,
      summaryHeading?: string,
      conversationIdsToLoad?: number[],
    ) => void;
    reportState: ReportState;
    taskSuggestions?: TaskSuggestion[];
    subheading: string;
    trackAnalyticsEvent?: (
      action: string,
      object: string,
      extraContext: Record<string, any>,
    ) => void;
  };
}

export default class AttributeSummaries extends Component<Signature> {
  @service declare contentSuggestionsService: ContentSuggestionsService;
  @service declare reportingChartService: ReportingChartService;

  @tracked suggestionCount = 0;
  @tracked topicsWithSuggestions: { name: string; count: number }[] = [];
  @tracked topicSuggestions: TrackedMap<string, ContentWrapper[]> = new TrackedMap();
  @tracked completedSuggestions = new Set<string>();

  @use metricResource = staticDataResource(this, {
    serializedChart: () => this.chartDefinition,
    reportState: () => this.args.reportState,
  });

  constructor(owner: any, args: any) {
    super(owner, args);
    taskFor(this.loadAllSuggestions).perform();
  }

  @restartableTask
  *loadAllSuggestions(): TaskGenerator<{ name: string; count: number }[]> {
    yield this.contentSuggestionsService.fetchTopicsSummary();

    let topicsWithSuggestions: { name: string; count: number }[] = [];

    for (let key of this.contentSuggestionsService.topicsSummary.keys()) {
      topicsWithSuggestions.push({
        name: key,
        count: this.contentSuggestionsService.topicsSummary.get(key)?.totalCount || 0,
      });
    }

    this.suggestionCount = topicsWithSuggestions.reduce((sum, topic) => sum + topic.count, 0);
    this.topicsWithSuggestions = topicsWithSuggestions;

    yield this.loadSuggestionsForAllTopics(topicsWithSuggestions);

    return topicsWithSuggestions;
  }

  get isLoading() {
    return this.metricResource.isLoading || taskFor(this.loadAllSuggestions).isRunning;
  }

  get sortTopicsWithSuggestions() {
    return [...this.topicsWithSuggestions].sort((a, b) => {
      if (b.count !== a.count) {
        return b.count - a.count;
      }
      // If there's a tie for suggestion count, sort by conversation count
      return this.getConversationCountForTopic(b.name) - this.getConversationCountForTopic(a.name);
    });
  }

  get topTopics() {
    return this.sortTopicsWithSuggestions.filter(
      (topic) => this.getConversationCountForTopic(topic.name) > 0,
    );
  }

  get chartDefinition() {
    return {
      view_by_other: false,
      view_by_display_limit: 5000,
      view_by: this.args.selectedJob.breakdownByAttributeId,
      chart_series: this.args.selectedJob.chartSeries.map((series: ChartSeriesDefinition) => ({
        ...series,
        filters: this.args.selectedJob.filters,
      })),
    };
  }

  get breakdownAttributeValues() {
    return Object.keys(this.metricResource.flatCombinedData);
  }

  get breakdownAttributeObjects(): { name: string }[] {
    return this.breakdownAttributeValues.map((name) => ({ name })).slice(0, 5);
  }

  get breakdownFullAttributeValues() {
    return Object.keys(this.metricResource.flatCombinedData);
  }

  get breakdownFullAttributeObjects(): { name: string }[] {
    return this.breakdownFullAttributeValues.map((name) => ({ name }));
  }

  get selectedTopicsWithSuggestions(): { name: string }[] {
    return this.topTopics.slice(0, 10);
  }

  get hasSubheading(): boolean {
    return this.args.subheading !== '';
  }

  //Usually we display up to 5 topics, but here we display up to 10 because of added suggestions
  get selectedTopicsWithoutSuggestions(): { name: string }[] {
    if (!this.metricResource.flatCombinedData) {
      return [];
    }
    if (this.canShowSuggestions) {
      let topicsWithSuggestionNames = new Set(this.topTopics.map((s) => s.name));
      return this.breakdownFullAttributeObjects
        .filter((item) => !topicsWithSuggestionNames.has(item.name))
        .slice(0, Math.max(0, 10 - this.topTopics.length));
    }
    //below is temporary until we integrate task suggestions with the other suggestions
    else if (this.args.selectedJob.id === 'dataGaps') {
      return this.breakdownAttributeObjects.slice(
        0,
        Math.max(0, 10 - this.dataGapTaskSuggestions.length),
      );
    } else if (this.args.selectedJob.id === 'actionGaps') {
      return this.breakdownAttributeObjects.slice(
        0,
        Math.max(0, 10 - this.actionGapTaskSuggestions.length),
      );
    } else {
      return this.breakdownAttributeObjects;
    }
  }

  get hasTopicsWithoutSuggestions(): boolean {
    return this.selectedTopicsWithoutSuggestions.length > 0;
  }

  get canShowSuggestions(): boolean {
    return this.suggestionCount > 0 && this.args.selectedJob.id === 'contentGaps';
  }

  get dataGapTaskSuggestions(): TaskSuggestion[] {
    let suggestions = this.args.taskSuggestions || [];
    return suggestions
      .filter((suggestion) => suggestion.isDataGap)
      .filter((suggestion) => this.suggestionHasTopicsWithConversations(suggestion));
  }

  get actionGapTaskSuggestions(): TaskSuggestion[] {
    let suggestions = this.args.taskSuggestions || [];
    return suggestions
      .filter((suggestion) => suggestion.isActionGap)
      .filter((suggestion) => this.suggestionHasTopicsWithConversations(suggestion));
  }

  get shouldShowDataGapTaskSuggestions(): boolean {
    return this.dataGapTaskSuggestions.length > 0 && this.args.selectedJob.id === 'dataGaps';
  }

  get shouldShowActionGapTaskSuggestions(): boolean {
    return this.actionGapTaskSuggestions.length > 0 && this.args.selectedJob.id === 'actionGaps';
  }

  get shouldShowContentGapSuggestions(): boolean {
    return this.canShowSuggestions && this.selectedTopicsWithSuggestions.length > 0;
  }

  get hasTaskSuggestions(): boolean {
    return this.dataGapTaskSuggestions.length > 0 || this.actionGapTaskSuggestions.length > 0;
  }

  get showEmptyState(): boolean {
    if (
      this.args.selectedJob.id === 'contentGaps' ||
      this.args.selectedJob.id === 'dataGaps' ||
      this.args.selectedJob.id === 'actionGaps'
    ) {
      return !(
        this.shouldShowActionGapTaskSuggestions ||
        this.shouldShowDataGapTaskSuggestions ||
        this.shouldShowContentGapSuggestions
      );
    }

    return this.selectedTopicsWithoutSuggestions.length === 0;
  }

  @action
  getConversationCountForTopic(breakdownAttributeValue: string): number {
    if (this.args.selectedJob.breakdownByAttributeId) {
      if (this.metricResource.isLoading || !this.metricResource.flatCombinedData) {
        return 0;
      }

      let data = this.metricResource.flatCombinedData[breakdownAttributeValue];
      if (!data || !data.length) {
        return 0;
      }

      return data[0] as number;
    }
    return 0;
  }

  private suggestionHasTopicsWithConversations(suggestion: TaskSuggestion): boolean {
    if (!suggestion.topicNameList) {
      return false;
    }

    return suggestion.topicNameList.some(
      (topicName) => this.getConversationCountForTopic(topicName) > 0,
    );
  }

  @action
  async loadSuggestionsForAllTopics(topics: { name: string; count: number }[]): Promise<void> {
    this.topicSuggestions.clear();

    let topicsWithSuggestions = topics.filter((topic) => topic.count > 0);

    for (let topic of topicsWithSuggestions) {
      try {
        let suggestions = await this.loadSingleTopicSuggestions(topic.name);
        this.topicSuggestions.set(topic.name, suggestions);
      } catch (error) {
        this.topicSuggestions.set(topic.name, []);
      }
    }
  }

  private async loadSingleTopicSuggestions(topicName: string): Promise<ContentWrapper[]> {
    await taskFor(this.contentSuggestionsService.loadSuggestionsForTopic).perform(topicName, null);

    return [...this.contentSuggestionsService.suggestionsForTopic];
  }

  @action
  getSuggestionsForTopicName(topicName: string): ContentWrapper[] {
    return this.topicSuggestions.get(topicName) || [];
  }

  @action
  isCompleted(suggestion: ContentWrapper): boolean {
    let key = `${suggestion.entityId}-${suggestion.entityType}`;

    if (this.completedSuggestions.has(key)) {
      return true;
    }

    let serviceResult = this.contentSuggestionsService.isSuggestionCompleted({
      entityId: suggestion.entityId!,
      entityType: suggestion.entityType,
    });

    if (serviceResult) {
      this.completedSuggestions.add(key);
    }

    return serviceResult;
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Reporting::Automation::AiInsights::Optimize::AttributeSummaries': typeof AttributeSummaries;
    'reporting/automation/ai-insights/optimize/attribute-summaries': typeof AttributeSummaries;
  }
}
